import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
  getSubscriptionSnapshot,
  includesFeature,
  refreshSubscriptionOnLogin,
  type SubscriptionFeature,
  type SubscriptionSnapshot,
} from '../services/subscription/subscriptionService';
import { subscriptionEn } from '../content/subscription.en';

interface SubscriptionContextType extends SubscriptionSnapshot {
  isReady: boolean;
  refresh: () => Promise<SubscriptionSnapshot>;
  canUse: (feature: SubscriptionFeature) => boolean;
  subscriptionRequiredMessage: string;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps): React.JSX.Element {
  const { user } = useAuth();
  const [snapshot, setSnapshot] = useState<SubscriptionSnapshot>(() => getSubscriptionSnapshot());
  const [isReady, setIsReady] = useState(false);

  const refresh = useCallback(async (): Promise<SubscriptionSnapshot> => {
    const next = await refreshSubscriptionOnLogin(user ?? null);
    setSnapshot(next);
    setIsReady(true);
    return next;
  }, [user]);

  const value = useMemo<SubscriptionContextType>(
    () => ({
      ...snapshot,
      isReady,
      refresh,
      canUse: (feature: SubscriptionFeature) => includesFeature(feature),
      subscriptionRequiredMessage: subscriptionEn.aiPlanRequired,
    }),
    [snapshot, isReady, refresh]
  );

  return (
    <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextType {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return ctx;
}

/** Convenience hook for a single feature flag. */
export function useSubscriptionFeature(feature: SubscriptionFeature): boolean {
  const { canUse } = useSubscription();
  return canUse(feature);
}
